// Centralized service data for Moneli
export const services = {
  cryptocurrency: {
    id: 'cryptocurrency',
    title: 'Custom Cryptocurrency & Token Economy',
    shortTitle: 'Build Your Digital Currency',
    subtitle: 'by Moneli',
    icon: 'fa-coins',
    tagline: 'Build Your Own Digital Currency & Reward Ecosystem',
    description: 'Transform your business with a custom cryptocurrency that drives customer loyalty, reduces costs, and creates a self-sustaining economic ecosystem.',
    shortDescription: 'Develop branded digital tokens on blockchain infrastructure to reward customers, reduce payment fees, and lock users into your platform.',

    // Hero section data
    hero: {
      title: 'Build Your Own Digital Currency & Reward Ecosystem',
      subtitle: 'Custom Cryptocurrency & Token Economy Development',
      description: 'Transform your business with a custom cryptocurrency that drives customer loyalty, reduces costs, and creates a self-sustaining economic ecosystem. We develop branded digital tokens on blockchain infrastructure, enabling you to reward customers, reduce payment fees, and lock users into your platform.'
    },

    // Perfect for section
    perfectFor: [
      { title: 'Educational Platforms', description: 'Reward students for course completion' },
      { title: 'E-commerce Businesses', description: 'Create loyalty points that customers can trade' },
      { title: 'Gaming Platforms', description: 'In-game currency with real-world value' },
      { title: 'Membership Communities', description: 'Exclusive token-based access tiers' },
      { title: 'Service Marketplaces', description: 'Internal currency for freelancers and buyers' },
      { title: 'SaaS Products', description: 'Credit systems and referral rewards' },
      { title: 'Fitness/Wellness Apps', description: 'Gamified achievement tokens' },
      { title: 'Real Estate', description: 'Fractional property ownership tokens' }
    ],

    // Development phases
    phases: [
      {
        number: 1,
        title: 'Strategy & Design',
        timeline: 'Week 1',
        items: [
          'Business model consultation',
          'Token economics design (supply, pricing, distribution)',
          'Use case mapping (how customers earn/spend tokens)',
          'Compliance review (legal/regulatory considerations)',
          'Brand naming and identity for your token'
        ]
      },
      {
        number: 2,
        title: 'Technical Development',
        timeline: 'Week 2-3',
        items: [
          'Custom token creation on TRON/Binance Smart Chain/Ethereum',
          'Smart contract development and deployment',
          'Wallet integration for your platform',
          'Payment gateway integration (token + fiat hybrid)',
          'Admin dashboard for token management',
          'API development for token transactions'
        ]
      },
      {
        number: 3,
        title: 'Platform Integration',
        timeline: 'Week 3-4',
        items: [
          'Integration with your existing website/app',
          'User wallet creation (automatic)',
          'Buy/sell/transfer functionality',
          'Transaction history and reporting',
          'Reward automation system',
          'Gamification features (leaderboards, achievements)'
        ]
      },
      {
        number: 4,
        title: 'Launch & Support',
        timeline: 'Week 4+',
        items: [
          'Security audit and testing',
          'Documentation and training',
          'Marketing materials (how to use guide)',
          '30 days post-launch support',
          'Performance monitoring dashboard'
        ]
      }
    ],

    // Key features
    features: [
      { icon: 'fa-palette', title: 'Your Brand, Your Rules', description: 'Fully customized token with your branding' },
      { icon: 'fa-piggy-bank', title: 'Cost Savings', description: 'Reduce payment processing fees by 70-90%' },
      { icon: 'fa-lock', title: 'Customer Lock-in', description: 'Users stay in your ecosystem to spend tokens' },
      { icon: 'fa-gamepad', title: 'Gamification', description: 'Boost engagement with rewards and achievements' },
      { icon: 'fa-shield-halved', title: 'Blockchain Verified', description: 'Transparent, secure, and tamper-proof' },
      { icon: 'fa-arrows-up-down-left-right', title: 'Scalable', description: 'Handles millions of transactions' },
      { icon: 'fa-mobile-screen', title: 'Multi-Platform', description: 'Works on web, mobile, and API' },
      { icon: 'fa-chart-line', title: 'Analytics Dashboard', description: 'Track token circulation and user behavior' }
    ],

    // Technical stack
    techStack: {
      blockchain: ['TRON (low fees)', 'Binance Smart Chain (flexible)', 'Ethereum (premium)'],
      tokenStandard: ['TRC-20', 'BEP-20', 'ERC-20'],
      smartContracts: 'Solidity',
      api: 'RESTful with comprehensive documentation',
      security: ['Multi-signature wallets', 'Encrypted storage'],
      integration: 'Compatible with any tech stack (React, Laravel, Python, etc.)'
    },

    // Pricing packages
    packages: [
      {
        tier: 'Starter',
        icon: '🥉',
        price: '$4,500',
        bestFor: 'Small businesses, MVPs',
        features: [
          'Token creation on TRON blockchain',
          'Basic buy/sell/transfer functionality',
          'Simple wallet integration',
          'Admin dashboard',
          '1 month support'
        ]
      },
      {
        tier: 'Business',
        icon: '🥈',
        price: '$8,500',
        bestFor: 'Growing businesses, e-commerce',
        featured: true,
        features: [
          'Everything in Starter +',
          'Advanced gamification (levels, rewards, bonuses)',
          'Payment gateway integration (Stripe + Token hybrid)',
          'Automated reward triggers',
          'Analytics dashboard',
          'Mobile app integration',
          '3 months support'
        ]
      },
      {
        tier: 'Enterprise',
        icon: '🥇',
        price: '$15,000+',
        bestFor: 'Large platforms, complex ecosystems',
        features: [
          'Everything in Business +',
          'Multi-token ecosystem (create multiple tokens)',
          'Exchange listing preparation',
          'Staking/interest system',
          'Partner merchant integration',
          'White-label wallet app',
          'Legal compliance consultation',
          '6 months support + maintenance'
        ]
      }
    ],

    // ROI examples
    roi: {
      costSavings: {
        title: 'Cost Savings Example',
        traditional: {
          fees: '3-5% per transaction',
          volume: '$500,000/year',
          feesPaid: '$15,000-25,000/year'
        },
        withToken: {
          fees: '~$100-500/year (blockchain fees only)',
          savings: '$14,500-24,500',
          payback: '3-6 months'
        }
      },
      revenueIncrease: {
        title: 'Revenue Increase Example',
        metrics: [
          { metric: 'Customer retention improvement', value: '+35%' },
          { metric: 'Average customer lifetime value', value: '+50%' },
          { metric: 'New revenue from token sales', value: '+20%' },
          { metric: 'Gamification engagement boost', value: '+60%' },
          { metric: 'Total business growth', value: '25-40% within first year' }
        ]
      }
    },

    // Success stories
    successStories: [
      {
        quote: 'After implementing our custom token, we saw a 45% increase in repeat customers and saved over $18,000 annually in payment fees.',
        company: 'E-learning Platform with 15,000 users'
      },
      {
        quote: 'The gamification features drove daily active users up by 67%. Students are addicted to earning tokens!',
        company: 'Online Fitness Coaching App'
      },
      {
        quote: 'Our token became a revenue stream itself. We now make 15% extra from token sales beyond our core business.',
        company: 'Membership Community Platform'
      }
    ],

    // Why choose us
    whyChooseUs: [
      { icon: 'fa-layer-group', text: 'Full-Stack Expertise - Blockchain + Web + Mobile development' },
      { icon: 'fa-bullseye', text: 'Business-First Approach - We design for profit, not just tech' },
      { icon: 'fa-certificate', text: 'Proven Track Record - Delivered token systems for ed-tech, e-commerce, gaming' },
      { icon: 'fa-scale-balanced', text: 'Compliance Aware - Navigate legal regulations safely' },
      { icon: 'fa-handshake', text: 'Ongoing Support - We\'re partners in your success' },
      { icon: 'fa-rocket', text: 'Fast Delivery - Launch in 4-6 weeks' }
    ],

    // Add-on services
    addOns: [
      { service: 'Token Marketing Strategy', price: '$1,500' },
      { service: 'Exchange Listing Support', price: '$3,000-5,000' },
      { service: 'Smart Contract Audit', price: '$2,000' },
      { service: 'Mobile Wallet App', price: '$4,000' },
      { service: 'Monthly Maintenance', price: '$500-1,500/mo' },
      { service: 'Token Economics Consulting', price: '$1,000' }
    ],

    // FAQs
    faqs: [
      {
        question: 'Is this legal?',
        answer: 'Yes, utility tokens for internal business use are legal in most jurisdictions. We provide compliance guidance.'
      },
      {
        question: 'Do customers need crypto knowledge?',
        answer: 'No! The token works like regular points. Users don\'t need to understand blockchain.'
      },
      {
        question: 'What if I don\'t have a tech platform yet?',
        answer: 'We can build the entire platform (website/app + token system) as a package deal.'
      },
      {
        question: 'Can customers cash out tokens for real money?',
        answer: 'You decide the rules. We can enable cash-out or keep it ecosystem-only.'
      },
      {
        question: 'How long does it take?',
        answer: '4-6 weeks for standard packages, 8-12 weeks for enterprise.'
      },
      {
        question: 'What if I want to switch blockchains later?',
        answer: 'We design for portability. Migrating is possible with our architecture.'
      }
    ]
  }
};

export default services;
